// The country filter: read a board for the projects created FROM one origin country.
//
// A popover multi-select rather than the device filter's toggle row, because the
// vocabulary is OPEN: it can run to dozens of countries, so it comes from the store
// (`countriesQuery`, the distinct codes with their project counts, most common
// first) rather than from a constant, and a row of toggles would not fit. It sits in
// the same header strips as the date range, the A/B group and the device toggles,
// and behaves like them: NOTHING selected means EVERY country, which is the default
// every board opens on and the cohort its cheap precomputed lane is materialized for.
//
// `unknown` is offered on purpose, for the device filter's reason: most of the table
// predates the stamp (migration 0195), plus every import and MCP turn, so a control
// that quietly excluded those rows would let a handful of ticked countries read as
// the whole population when it is a slice of it. It is listed LAST, as the absence
// bucket rather than a peer of the real countries.

import { useQuery } from "@tanstack/react-query";
import { MultiSelect, type MultiSelectOption } from "@recruiter/shared";

import { UNKNOWN_COUNTRY, countryFilterOptions, countryLabel } from "../lib/country";
import { countriesQuery } from "../lib/queries";
import "./country-filter.css";

type Props = {
  /** The selection, in the caller's own state (URL or restored). Empty = all. */
  countries: readonly string[];
  onChange: (next: readonly string[]) => void;
};

/** The options a TABLE's country column offers in server mode: the store's
 *  vocabulary plus the null bucket, off the same cached facet this control reads.
 *  One hook, so the Explorer, the two worklists and the Searches list cannot list
 *  it four ways — and none of them has to import the query itself. */
export function useCountryFilterOptions(): MultiSelectOption[] {
  const facets = useQuery(countriesQuery());
  return countryFilterOptions(facets.data?.countries);
}

export function CountryFilter({ countries, onChange }: Props) {
  const facets = useQuery(countriesQuery());
  const known = facets.data?.countries ?? [];
  // A stale link can name a country the facet no longer lists (its projects were
  // deleted, or the cache is minutes behind a first arrival). It stays tickable
  // so the reader can see it and clear it, rather than a filter with no control.
  const stragglers = countries.filter(
    (code) => code !== UNKNOWN_COUNTRY && !known.some((facet) => facet.code === code),
  );
  const options: MultiSelectOption[] = [
    ...known.map((facet) => ({ value: facet.code, label: countryLabel(facet.code) })),
    ...stragglers.map((code) => ({ value: code, label: countryLabel(code) })),
    { value: UNKNOWN_COUNTRY, label: countryLabel(UNKNOWN_COUNTRY) },
  ];

  return (
    <div className="cf-filter">
      <span className="cf-filter__label">Country</span>
      <MultiSelect
        label="Country"
        allLabel="All countries"
        options={options}
        selected={countries}
        onChange={onChange}
      />
    </div>
  );
}
